/*
 * Bisglycinat-Test — calculator that checks a customer's own magnesium
 * bisglycinate product for provable dilution.
 *
 * Method (identical to PRODUCT.md, the one published with the comparison articles):
 *   unit weight    = net fill ÷ unit count (capsules, tablets or powder servings)
 *   mg per unit    = magnesium per daily dose ÷ units per daily dose
 *   reference mass = declared compound amount, otherwise
 *                      capsule:        unit weight − 120 mg shell
 *                      tablet/powder:  full unit weight, no deduction
 *                    minus declared amounts of other ingredients (fallback branch only)
 *   share          = mg per unit ÷ reference mass
 *   foreign share  = max(0; (mg − 0.141 × reference mass) ÷ mg)
 *   fallback share below 10.0 % = not assessable (mostly non-compound mass,
 *   neither dilution nor purity can be stated)
 *
 * 0.141 = 24.305 g/mol magnesium ÷ 172.42 g/mol magnesium bisglycinate
 * (PubChem CID 84645, CAS 14783-68-7). A share above that limit cannot come
 * from bisglycinate alone, so the excess is arithmetically provable.
 *
 * One-way test: a value below the limit proves nothing about purity, only that
 * no dilution can be shown by calculation. All wording follows from that.
 */

/* Plausibility check — does the stated fill weight even fit into a capsule?
   Size 000 is the most favourable assumption (largest capsule), so a failure
   here means the label data cannot be right regardless of capsule size. */
const HULL_000_MG = 146;   // HPMC shell, size 000
const VOLUME_000_ML = 1.37;
const DENSITY_IMPOSSIBLE = 0.95;  // g/ml — bisglycinate powder tops out ~0.85
const DENSITY_BORDERLINE = 0.85;
const MGO_MAX_SHARE = 0.603;      // magnesium oxide, the richest magnesium compound
const MIN_PLAUSIBLE_SHARE = 0.01; // below this the entered magnesium value cannot be milligrams
const SHELL_MG = 120;             // flat capsule-shell deduction (PRODUCT.md, 2026-09-18)
const MIN_ASSESSABLE_SHARE = 0.10; // lowest traded bisglycinate grade (PRODUCT.md, 2026-09-28)
const TRADE_MAX_SHARE = 0.1166;   // dihydrate, the driest traded form; above it only near-anhydrous raw material fits
/* Scale shown with the result: x-axis range and BISMAXINAT's own share for reference */
const SCALE_MIN = 0.08, SCALE_MAX = 0.18, OWN_SHARE = 0.105;

class BisglycinatTest extends HTMLElement {
  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;

    const cfgEl = this.querySelector('script[type="application/json"][data-bgt-config]');
    if (!cfgEl) return;
    try {
      // The Liquid `t` filter HTML-escapes translations (an apostrophe arrives as &#39;);
      // every string is escaped again on render, so decode once here.
      this.config = decodeEntities(JSON.parse(cfgEl.textContent));
    } catch (e) {
      console.error('[bisglycinat-test] config parse failed', e);
      return;
    }

    this.stage = this.querySelector('[data-bgt-stage]');
    this.buySelector = this.dataset.buySelector || '.product-form';
    this.productHandle = this.dataset.productHandle || '';
    this.limit = typeof this.config.limit === 'number' ? this.config.limit : 0.141;
    this.history = [];
    this.startedAt = null;
    this.metrics = null;
    this.values = {};

    this.go('intro', { initial: true });
  }

  go(ref, { initial } = {}) {
    const node = this.config.nodes[ref];
    if (!node) return;
    if (!initial) this.history.push(this.current);
    this.current = ref;
    this.render(node);
    // No auto-scroll on transitions — content fades in place. Scroll is only
    // triggered explicitly by the buy CTA at the end (scrollToBuy).

    if (ref === 'intro') return;
    if (!this.startedAt && node.type === 'input') {
      this.startedAt = Date.now();
      this.track('bisglycinat_test_start');
    }
    if (node.type === 'result') {
      const durationMs = this.startedAt ? Date.now() - this.startedAt : null;
      this.track('bisglycinat_test_complete', {
        outcome: node.outcome,
        duration_ms: durationMs,
        share_pct: this.metrics ? round1(this.metrics.share * 100) : null,
        foreign_share_pct: this.metrics ? round1(this.metrics.foreign * 100) : null,
      });
    }
  }

  back() {
    const prev = this.history.pop();
    if (!prev) return;
    const node = this.config.nodes[prev];
    if (!node) return;
    this.current = prev;
    this.render(node);
  }

  retake() {
    this.history = [];
    this.startedAt = null;
    this.metrics = null;
    this.values = {};
    this.track('bisglycinat_test_retake');
    this.go('intro', { initial: true });
  }

  scrollToBuy() {
    const target = document.querySelector(this.buySelector);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (this.productHandle) {
      // No product form on this page — navigate to the product page, preserving locale prefix.
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      window.location.href = `${root.replace(/\/$/, '')}/products/${this.productHandle}`;
      return;
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    this.track('bisglycinat_test_buy_click', { outcome: this.current });
  }

  track(event, payload = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, product_handle: this.productHandle, ...payload });
  }

  // --- the calculation ---

  /* Returns { outcome, metrics } — metrics carry every intermediate value so the
     result screen can show the full working rather than just a verdict. */
  compute(v) {
    const isCapsule = (v.dosage_form || 'capsule') === 'capsule';
    const capsuleWeight = (v.net_weight * 1000) / v.capsules;   // mg per unit
    const mgPerCapsule = v.mg_per_day / v.caps_per_day;         // mg
    const declared = v.compound_per_day ? v.compound_per_day / v.caps_per_day : null;
    // Shell and other declared ingredients only matter in the fallback branch;
    // a declared compound amount already is the reference mass.
    const shell = !declared && isCapsule ? SHELL_MG : 0;
    const other = !declared && v.other_per_day ? v.other_per_day / v.caps_per_day : 0;
    const reference = declared || (capsuleWeight - shell - other);
    const share = mgPerCapsule / reference;
    const foreign = Math.max(0, (mgPerCapsule - this.limit * reference) / mgPerCapsule);
    const density = (capsuleWeight - HULL_000_MG) / VOLUME_000_ML / 1000;  // g/ml

    const metrics = {
      capsuleWeight, mgPerCapsule, declared, reference, share, foreign, density, shell, other,
      usedDeclared: Boolean(declared),
      borderlineDensity: isCapsule && density >= DENSITY_BORDERLINE && density <= DENSITY_IMPOSSIBLE,
    };

    // Hard contradictions — the numbers cannot describe a real product, so no
    // verdict is offered at all. Never blame the manufacturer here; a typo in
    // the input or a relabelled formula is the likelier explanation.
    const hard =
      reference <= 0 ||
      (isCapsule && capsuleWeight <= HULL_000_MG) ||
      share > MGO_MAX_SHARE ||
      share < MIN_PLAUSIBLE_SHARE ||
      (declared && declared > capsuleWeight);

    if (hard) return { outcome: 'result_data_impossible', metrics, hard: true };

    // Soft contradiction: the fill would not fit a size-000 capsule. Shown as a
    // warning with the option to see the result anyway (the product may be
    // tablets, which are pressed and reach far higher densities).
    if (isCapsule && density > DENSITY_IMPOSSIBLE) {
      return { outcome: 'result_data_impossible', metrics, hard: false };
    }

    return { outcome: this.verdict(metrics), metrics };
  }

  verdict(metrics) {
    // Fallback branch below the lowest traded grade: the reference mass is mostly
    // something other than the compound, so no statement either way.
    if (!metrics.usedDeclared && metrics.share < MIN_ASSESSABLE_SHARE) return 'result_not_assessable';

    // Below 0.1 % the foreign share is indistinguishable from rounding noise —
    // resolved in favour of the tested product, like every other assumption here.
    if (metrics.foreign * 100 >= 0.1) return 'result_diluted';
    // Clean, but above the driest traded grade: only near-anhydrous raw material
    // explains it. A chemistry note, never a suspicion (PRODUCT.md, Baustein B).
    if (metrics.share > TRADE_MAX_SHARE) return 'result_above_trade';
    return 'result_no_dilution_provable';
  }

  submitForm(node) {
    const values = {};
    let firstError = null;

    node.groups.forEach(group => group.fields.forEach(field => {
      if (field.type === 'choice') {
        const checked = this.stage.querySelector(`[name="bgt-${field.key}"]:checked`);
        values[field.key] = checked ? checked.value : field.options[0].value;
        return;
      }
      const input = this.stage.querySelector(`[data-bgt-field="${field.key}"]`);
      const wrap = input.closest('.bgt-field');
      const errorEl = wrap.querySelector('[data-bgt-error]');
      const raw = input.value.trim();
      errorEl.textContent = '';
      wrap.classList.remove('bgt-field--invalid');

      if (!raw) {
        if (field.optional) { values[field.key] = null; return; }
        errorEl.textContent = node.errors.required;
        wrap.classList.add('bgt-field--invalid');
        firstError = firstError || input;
        return;
      }

      const num = parseLocaleNumber(raw);
      if (!(num > 0)) {
        errorEl.textContent = node.errors.positive;
        wrap.classList.add('bgt-field--invalid');
        firstError = firstError || input;
        return;
      }
      values[field.key] = num;
    }));

    if (firstError) { firstError.focus(); return; }

    this.values = values;
    const { outcome, metrics, hard } = this.compute(values);
    this.metrics = metrics;
    this.impossibleIsHard = Boolean(hard);
    // Remember what the numbers say, so "show anyway" can reveal it after a
    // soft plausibility warning.
    this.rawVerdict = this.verdict(metrics);
    this.go(outcome);
  }

  // --- rendering ---

  render(node) {
    const html = node.type === 'intro'  ? this.renderIntro(node)
              : node.type === 'input'  ? this.renderForm(node)
              : node.type === 'result' ? this.renderResult(node)
              : '';

    this.stage.classList.add('bgt-fade-out');
    setTimeout(() => {
      this.stage.innerHTML = html;
      this.bindEvents(node);
      this.stage.classList.remove('bgt-fade-out');
    }, 120);
  }

  disclaimerHtml() {
    return `<p class="bgt-disclaimer">${escapeHtml(this.config.disclaimer)}</p>`;
  }

  renderIntro(node) {
    return `
      <div class="bgt-card bgt-card--intro">
        <div class="bgt-card__inner">
          <h2 class="bgt-heading">${escapeHtml(this.fillPlaceholders(node.heading))}</h2>
          ${formatBody(this.fillPlaceholders(node.body))}
          <button type="button" class="bgt-btn bgt-btn--primary" data-bgt-action="next" data-bgt-target="${escapeHtml(node.next)}">
            ${escapeHtml(node.cta)}
          </button>
        </div>
        ${this.disclaimerHtml()}
      </div>
    `;
  }

  renderForm(node) {
    const groups = node.groups.map(group => `
      <fieldset class="bgt-group">
        <legend class="bgt-group__legend">${escapeHtml(group.label)}</legend>
        ${group.fields.map(field => field.type === 'choice' ? `
          <div class="bgt-field bgt-field--choice" role="radiogroup" aria-label="${escapeHtml(field.label)}">
            <span class="bgt-field__label">${escapeHtml(field.label)}</span>
            <div class="bgt-choice">
              ${field.options.map((o, i) => `
                <label class="bgt-choice__opt">
                  <input type="radio" name="bgt-${escapeHtml(field.key)}" value="${escapeHtml(o.value)}" ${(this.values[field.key] || field.options[0].value) === o.value ? 'checked' : ''}>
                  <span>${escapeHtml(o.label)}</span>
                </label>`).join('')}
            </div>
          </div>
        ` : `
          <div class="bgt-field">
            <label class="bgt-field__label" for="bgt-${escapeHtml(field.key)}">${escapeHtml(field.label)}</label>
            <div class="bgt-field__control">
              <input
                class="bgt-field__input"
                id="bgt-${escapeHtml(field.key)}"
                data-bgt-field="${escapeHtml(field.key)}"
                type="text"
                inputmode="decimal"
                autocomplete="off"
                value="${this.values[field.key] != null ? escapeHtml(formatNumber(this.values[field.key], 2)) : ''}">
              ${field.unit ? `<span class="bgt-field__unit">${escapeHtml(field.unit)}</span>` : ''}
            </div>
            <p class="bgt-field__hint">${escapeHtml(field.hint)}</p>
            <p class="bgt-field__error" data-bgt-error></p>
          </div>
        `).join('')}
      </fieldset>
    `).join('');

    return `
      <div class="bgt-card bgt-card--form">
        <div class="bgt-card__inner">
          <h3 class="bgt-heading">${escapeHtml(this.fillPlaceholders(node.heading))}</h3>
          ${formatBody(this.fillPlaceholders(node.body))}
          <form class="bgt-form" data-bgt-form novalidate>
            ${groups}
            <button type="submit" class="bgt-btn bgt-btn--primary bgt-btn--block">${escapeHtml(node.submit)}</button>
          </form>
          <div class="bgt-actions">
            <button type="button" class="bgt-btn bgt-btn--ghost" data-bgt-action="choose" data-bgt-target="result_no_data">${escapeHtml(node.noData)}</button>
          </div>
        </div>
        ${this.disclaimerHtml()}
      </div>
    `;
  }

  /* The working, shown line by line. This is the point of the whole rewrite:
     a number the customer can recalculate beats a verdict they have to trust. */
  renderCalc() {
    const m = this.metrics;
    if (!m) return '';
    const c = this.config.calc;
    const v = this.values;

    const rows = [
      [c.capsule_weight, ltr(`${formatNumber(v.net_weight, 2)} g ÷ ${formatNumber(v.capsules, 0)} = ${formatNumber(m.capsuleWeight, 1)} mg`)],
      [c.mg_per_capsule, ltr(`${formatNumber(v.mg_per_day, 1)} mg ÷ ${formatNumber(v.caps_per_day, 0)} = ${formatNumber(m.mgPerCapsule, 1)} mg`)],
      ...(m.shell ? [[c.shell, ltr(`− ${formatNumber(m.shell, 0)} mg`)]] : []),
      ...(m.other ? [[c.other, ltr(`− ${formatNumber(m.other, 1)} mg`)]] : []),
      [m.usedDeclared ? c.reference_mass_declared : c.reference_mass, ltr(`${formatNumber(m.reference, 1)} mg`)],
      [c.share, ltr(`${formatNumber(m.mgPerCapsule, 1)} ÷ ${formatNumber(m.reference, 1)} = ${formatPercent(m.share, 1)}`)],
      [c.limit, ltr(formatPercent(this.limit, 2))],
      [c.foreign_share, ltr(formatPercent(m.foreign, 1))],
    ];

    const note = m.borderlineDensity ? `<p class="bgt-calc__note">${escapeHtml(c.density_note)}</p>` : '';

    return `
      <div class="bgt-calc">
        <h4 class="bgt-calc__heading">${escapeHtml(c.heading)}</h4>
        <dl class="bgt-calc__list">
          ${rows.map(([label, value]) => `
            <div class="bgt-calc__row">
              <dt>${escapeHtml(label)}</dt>
              <dd>${escapeHtml(value)}</dd>
            </div>
          `).join('')}
        </dl>
        ${note}
        <p class="bgt-calc__source">${escapeHtml(c.source)}</p>
      </div>
    `;
  }

  /* Where the product sits between traded raw material, the chemical limit and
     dilution — the same bands the comparison articles grade with. */
  renderScale() {
    const m = this.metrics;
    const sc = this.config.scale;
    if (!m || !sc) return '';
    const pos = v => (Math.min(Math.max(v, SCALE_MIN), SCALE_MAX) - SCALE_MIN) / (SCALE_MAX - SCALE_MIN) * 100;
    const zones = [
      ['na', SCALE_MIN, MIN_ASSESSABLE_SHARE, sc.not_assessable],
      ['trade', MIN_ASSESSABLE_SHARE, TRADE_MAX_SHARE, sc.trade],
      ['dry', TRADE_MAX_SHARE, this.limit, sc.dry],
      ['diluted', this.limit, SCALE_MAX, sc.diluted],
    ];
    const ticks = [MIN_ASSESSABLE_SHARE, TRADE_MAX_SHARE, this.limit];
    return `
      <div class="bgt-scale">
        <h4 class="bgt-calc__heading">${escapeHtml(sc.heading)}</h4>
        <div class="bgt-scale__track">
          ${zones.map(([k, a, b]) => `<span class="bgt-scale__zone bgt-scale__zone--${k}" style="left:${pos(a)}%;width:${pos(b) - pos(a)}%"></span>`).join('')}
          <span class="bgt-scale__mark bgt-scale__mark--own" style="left:${pos(OWN_SHARE)}%" title="BISMAXINAT"></span>
          <span class="bgt-scale__mark bgt-scale__mark--you" style="left:${pos(m.share)}%"><b>${escapeHtml(ltr(formatPercent(m.share, 1)))}</b></span>
        </div>
        <div class="bgt-scale__ticks">${ticks.map(t => `<span style="left:${pos(t)}%">${escapeHtml(ltr(formatRounded(t * 100, t === this.limit ? 2 : 1) + ' %'))}</span>`).join('')}</div>
        <ul class="bgt-scale__legend">
          ${zones.map(([k, , , label]) => `<li><i class="bgt-scale__dot bgt-scale__zone--${k}"></i>${escapeHtml(label)}</li>`).join('')}
          <li><i class="bgt-scale__dot bgt-scale__dot--you"></i>${escapeHtml(sc.you)}</li>
          <li><i class="bgt-scale__dot bgt-scale__dot--own"></i>${escapeHtml(sc.own)}</li>
        </ul>
        <p class="bgt-calc__note">${escapeHtml(sc.note)}</p>
      </div>
    `;
  }

  renderResult(node) {
    const toneClass = node.ctaTone === 'soft' ? 'bgt-btn--soft' : 'bgt-btn--primary';
    const body = formatBody(this.fillPlaceholders(node.body));
    const calc = node.showCalc ? this.renderScale() + this.renderCalc() : '';
    const showAnyway = node.showAnyway && !this.impossibleIsHard
      ? `<button type="button" class="bgt-btn bgt-btn--ghost" data-bgt-action="choose" data-bgt-target="${escapeHtml(this.rawVerdict)}">${escapeHtml(this.config.buttons.showAnyway)}</button>`
      : '';

    return `
      <div class="bgt-card bgt-card--result bgt-card--result-${escapeHtml(node.outcome)}">
        <div class="bgt-result__badge bgt-result__badge--${escapeHtml(node.outcome)}"></div>
        <div class="bgt-card__inner">
          <h3 class="bgt-heading">${escapeHtml(this.fillPlaceholders(node.heading))}</h3>
          ${body}
          ${calc}
          <div class="bgt-actions bgt-actions--column">
            <button type="button" class="bgt-btn ${toneClass}" data-bgt-action="buy">
              ${escapeHtml(this.config.buttons.buy)}
              <span class="bgt-btn__arrow" aria-hidden="true">↑</span>
            </button>
            ${showAnyway}
            <button type="button" class="bgt-btn bgt-btn--ghost" data-bgt-action="retake">
              ${escapeHtml(this.config.buttons.retake)}
            </button>
          </div>
        </div>
        ${this.disclaimerHtml()}
      </div>
    `;
  }

  fillPlaceholders(text) {
    if (text == null) return '';
    const m = this.metrics;
    const map = {
      '{limit}': ltr(formatPercent(this.limit, 2)),
      '{share}': m ? ltr(formatPercent(m.share, 1)) : '',
      '{foreign_share}': m ? ltr(formatPercent(m.foreign, 1)) : '',
      '{capsule_weight}': m ? ltr(`${formatNumber(m.capsuleWeight, 1)} mg`) : '',
      '{mg_per_capsule}': m ? ltr(`${formatNumber(m.mgPerCapsule, 1)} mg`) : '',
    };
    return String(text).replace(/\{(limit|share|foreign_share|capsule_weight|mg_per_capsule)\}/g, k => map[k]);
  }

  bindEvents(node) {
    const form = this.stage.querySelector('[data-bgt-form]');
    if (form) {
      form.addEventListener('submit', e => {
        e.preventDefault();
        this.submitForm(node);
      });
    }

    this.stage.querySelectorAll('[data-bgt-action]').forEach(el => {
      el.addEventListener('click', e => {
        const action = e.currentTarget.dataset.bgtAction;
        const target = e.currentTarget.dataset.bgtTarget;
        if (action === 'next' || action === 'choose') {
          this.go(target);
        } else if (action === 'back') {
          this.back();
        } else if (action === 'retake') {
          this.retake();
        } else if (action === 'buy') {
          this.scrollToBuy();
        }
      });
    });
  }
}

function decodeEntities(v) {
  if (typeof v === 'string') {
    if (!/&(#\d+|#x[0-9a-f]+|quot|amp|lt|gt|apos);/i.test(v)) return v;
    const ta = document.createElement('textarea');
    ta.innerHTML = v;
    return ta.value;
  }
  if (Array.isArray(v)) return v.map(decodeEntities);
  if (v && typeof v === 'object') {
    const out = {};
    for (const k of Object.keys(v)) out[k] = decodeEntities(v[k]);
    return out;
  }
  return v;
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

/* Body text supports **bold** segments and \n\n paragraph breaks. */
function formatBody(s) {
  if (s == null) return '';
  const esc = escapeHtml(s);
  const bolded = esc.replace(/\*\*(.+?)\*\*/gs, '<strong>$1</strong>');
  return bolded.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean).map(p => `<p class="bgt-body">${p}</p>`).join('');
}

/* Accepts what people actually type across our markets: "169,2", "169.2" and
   grouped thousands like "1.410" or "1,410". Grouping is only assumed for the
   strict pattern (1-3 digits, then groups of exactly 3) so "42.5" stays 42.5. */
function parseLocaleNumber(raw) {
  let s = String(raw).replace(/[^\d.,-]/g, '');
  if (/^-?[1-9]\d{0,2}([.,]\d{3})+$/.test(s)) return Number(s.replace(/[.,]/g, ''));
  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');
  const decimalAt = Math.max(lastDot, lastComma);
  if (decimalAt === -1) return Number(s);
  const intPart = s.slice(0, decimalAt).replace(/[.,]/g, '');
  const fracPart = s.slice(decimalAt + 1).replace(/[.,]/g, '');
  return Number(`${intPart}.${fracPart}`);
}

function locale() {
  const loc = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'de';
  // Keep Latin digits in Arabic: the calculated values sit next to the CAS and
  // PubChem numbers, which stay Latin either way.
  return /^ar\b/.test(loc) ? 'ar-u-nu-latn' : loc;
}

/* Truncated, never rounded up — the conservative direction throughout: it can
   only ever understate the foreign share, never overstate it. */
function truncate(value, decimals) {
  const f = Math.pow(10, decimals);
  // The epsilon absorbs binary float artefacts only (0.141 * 100 lands at
  // 14.099999999999998, which would otherwise print as 14,09 %). It is far too
  // small to pull a genuine 14.0999 up to 14.10.
  return Math.trunc(value * f + 1e-9) / f;
}

function round1(value) {
  return Math.round(value * 10) / 10;
}

function formatNumber(value, decimals) {
  const truncated = truncate(value, decimals);
  try {
    return new Intl.NumberFormat(locale(), {
      minimumFractionDigits: decimals, maximumFractionDigits: decimals,
    }).format(truncated);
  } catch (e) {
    return truncated.toFixed(decimals);
  }
}

/* Rounded (not truncated) — only for the fixed scale anchors, e.g. 11.66 → "11,7". */
function formatRounded(value, decimals) {
  try {
    return new Intl.NumberFormat(locale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
  } catch (e) {
    return value.toFixed(decimals);
  }
}

function formatPercent(value, decimals) {
  return `${formatNumber(value * 100, decimals)} %`;
}

/* Wrap a figure or formula in a left-to-right isolate. Without it the bidi
   algorithm reorders "60,00 g ÷ 100 = 600,0 mg" inside an Arabic paragraph and
   the equation comes out scrambled. The control characters are invisible and
   inert in left-to-right locales. */
function ltr(s) {
  return `\u2066${s}\u2069`;
}

customElements.define('bisglycinat-test', BisglycinatTest);
